import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePageParam } from "@/hooks/use-page-param";
import { Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ListPagination } from "@/components/ui/list-pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAgencias } from "@/hooks/use-agencias";
import {
  DEFAULT_VAGA_LIST_FILTERS,
  fetchAllVagasForExport,
  useVagasListCount,
  useVagasListPage,
  type VagaListFilters,
} from "@/hooks/use-vagas-list";
import { formatDateBR, formatDateOnly } from "@/utils/dateUtils";
import { getVagaStatusTitle } from "@/utils/vaga-status";
import { toCsv, downloadTextFile } from "@/utils/csv";
import { VagasListFilters } from "./VagasListFilters";
import { VagasListTable } from "./VagasListTable";
import { trackEvent } from "@/lib/telemetry";
import { decisaoIso, formatDeltaAbrev, formatVerba } from "./vagaRowUtils";

const FILTERS_KEY = "vagas_list_filters";
const FREELANCER_KEY = "vagas_list_freelancer";
const PAGE_SIZE_KEY = "vagas.list_page_size";

interface FreelancerOption {
  id: string;
  nome: string;
}

function readFilters(): VagaListFilters {
  if (typeof window === "undefined") return DEFAULT_VAGA_LIST_FILTERS;
  try {
    const raw = window.localStorage.getItem(FILTERS_KEY);
    if (!raw) return DEFAULT_VAGA_LIST_FILTERS;
    return { ...DEFAULT_VAGA_LIST_FILTERS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_VAGA_LIST_FILTERS;
  }
}

function readFreelancer(): FreelancerOption | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(FREELANCER_KEY);
    return raw ? (JSON.parse(raw) as FreelancerOption) : null;
  } catch {
    return null;
  }
}

function countActive(filters: VagaListFilters): number {
  let count = 0;
  if (filters.search.trim()) count++;
  if (filters.agenciaId) count++;
  if (filters.statuses.length) count++;
  if (filters.freelancerId) count++;
  if (filters.dateField && (filters.dateFrom || filters.dateTo)) count++;
  return count;
}

export function VagasListView() {
  const { data: agencias = [] } = useAgencias();
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);

  const [filters, setFilters] = useState<VagaListFilters>(readFilters);
  const [freelancer, setFreelancer] = useState<FreelancerOption | null>(readFreelancer);
  const [searchInput, setSearchInput] = useState<string>(() => readFilters().search);

  const [pageSize, setPageSize] = useState<number>(() => {
    const saved = window.localStorage.getItem(PAGE_SIZE_KEY);
    return saved ? Math.max(10, parseInt(saved, 10)) : 30;
  });

  const [currentPage, setCurrentPage] = usePageParam();

  // Persistência
  useEffect(() => {
    window.localStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
  }, [filters]);
  useEffect(() => {
    if (freelancer) window.localStorage.setItem(FREELANCER_KEY, JSON.stringify(freelancer));
    else window.localStorage.removeItem(FREELANCER_KEY);
  }, [freelancer]);
  useEffect(() => {
    window.localStorage.setItem(PAGE_SIZE_KEY, pageSize.toString());
  }, [pageSize]);

  // Patch de filtros: mescla, e reseta a página pra 1 (só se já não estiver nela).
  const patchFilters = useCallback(
    (patch: Partial<VagaListFilters>) => {
      setFilters((prev) => ({ ...prev, ...patch }));
      if (currentPage !== 1) setCurrentPage(1);
    },
    [currentPage, setCurrentPage],
  );

  // Debounce da busca -> filtros. Pula a 1ª execução (input === filtro salvo)
  // pra não resetar a página de um deep-link.
  const didMountSearch = useRef(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!didMountSearch.current) {
        didMountSearch.current = true;
        if (searchInput === filters.search) return;
      }
      patchFilters({ search: searchInput });
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const handleFreelancerChange = useCallback(
    (option: FreelancerOption | null) => {
      setFreelancer(option);
      patchFilters({ freelancerId: option?.id ?? null });
    },
    [patchFilters],
  );

  const handleClear = useCallback(() => {
    setFilters(DEFAULT_VAGA_LIST_FILTERS);
    setFreelancer(null);
    setSearchInput("");
    if (currentPage !== 1) setCurrentPage(1);
  }, [currentPage, setCurrentPage]);

  const { data: vagas = [], isLoading, isFetching, isError } = useVagasListPage(
    filters,
    currentPage,
    pageSize,
  );
  const { data: filteredCount = 0, isPending: isCountPending } =
    useVagasListCount(filters);

  const totalPages = Math.max(1, Math.ceil(filteredCount / pageSize));
  const activeCount = useMemo(() => countActive(filters), [filters]);

  // Se a contagem encolher abaixo da página atual, volta pra última válida.
  useEffect(() => {
    // Antes do count resolver, totalPages=1 e o clamp mataria ?page=30.
    if (isCountPending) return;
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [isCountPending, currentPage, totalPages, setCurrentPage]);

  // Exporta TODAS as vagas que batem nos filtros atuais (não só a página) em CSV.
  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const linhas = await fetchAllVagasForExport(filters);
      const headers = [
        "Agência", "ID", "Área", "Verba", "Status", "Deadline",
        "Shortlist", "Aprovados", "Freelancer aprovado",
        "Cadastro", "Aprovação/Reprovação", "Tempo até decisão",
      ];
      const rows = linhas.map((vaga) => {
        const decisao = decisaoIso(vaga);
        return [
          vaga.agencia ?? "",
          vaga.id,
          vaga.areas ?? "",
          formatVerba(vaga),
          getVagaStatusTitle(vaga.status),
          vaga.deadline_preenchimento ? formatDateBR(vaga.deadline_preenchimento) : "",
          vaga.qtd_shortlist ?? 0,
          vaga.qtd_aprovados_kanban ?? 0,
          vaga.aprovado_kanban_freelancer_nome ?? "",
          vaga.created_at ? formatDateOnly(vaga.created_at) : "",
          decisao ? formatDateOnly(decisao) : "",
          decisao && vaga.created_at ? (formatDeltaAbrev(vaga.created_at, decisao) ?? "") : "",
        ];
      });
      const hoje = new Date().toISOString().slice(0, 10);
      downloadTextFile(`vagas-${hoje}.csv`, toCsv(headers, rows));
      trackEvent("lista.exportar", { contexto: { lista: "vagas", total: rows.length } });
      toast({ title: "Exportado", description: `${rows.length} ${rows.length === 1 ? "vaga exportada" : "vagas exportadas"}.` });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao exportar",
        description: "Não foi possível gerar o CSV. Tente novamente.",
      });
    } finally {
      setIsExporting(false);
    }
  }, [filters, toast]);

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto pb-6 pt-3">
      <VagasListFilters
        searchInput={searchInput}
        onSearchInput={setSearchInput}
        filters={filters}
        onPatch={patchFilters}
        agencias={agencias}
        freelancer={freelancer}
        onFreelancerChange={handleFreelancerChange}
        activeCount={activeCount}
        onClear={handleClear}
      />

      <div className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          {isFetching && <Loader2 className="h-4 w-4 animate-spin" />}
          <span>
            {filteredCount} {filteredCount === 1 ? "vaga" : "vagas"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-2 bg-white"
            onClick={handleExport}
            disabled={isExporting || filteredCount === 0}
          >
            {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Exportar CSV
          </Button>
          <span className="hidden sm:inline">Por página</span>
          <Select value={pageSize.toString()} onValueChange={(value) => { setPageSize(parseInt(value, 10)); if (currentPage !== 1) setCurrentPage(1); }}>
            <SelectTrigger className="w-[80px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[15, 30, 60, 100].map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700">
          Erro ao carregar as vagas. Tente novamente.
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" /> Carregando vagas...
        </div>
      ) : vagas.length === 0 ? (
        <div className="rounded-lg border border-dashed p-12 text-center text-sm text-muted-foreground">
          Nenhuma vaga encontrada com os filtros atuais.
        </div>
      ) : (
        <VagasListTable vagas={vagas} />
      )}

      {totalPages > 1 && (
        <ListPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
}
